/**
 * A barra lateral de arquivos, portada de app.js:494-524.
 *
 * Duas coisas que pareciam detalhe e não são: o filtro que esconde tudo precisa
 * dizer por quê (esconder sem explicar faz o dev achar que o projeto perdeu os
 * arquivos), e a pastilha de notas ao lado do arquivo é o que mostra onde há
 * conhecimento humano antes de abrir a doc.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useContagemDeNotas, useDocs } from '../api/consultas';

const COR_DA_ESPECIE: Record<string, string> = {
  overview: 'var(--brand)',
  agents: 'var(--violet)',
};

export function Sidebar({ slug }: { slug: string }) {
  const docs = useDocs(slug);
  const notas = useContagemDeNotas(slug);
  const navegar = useNavigate();
  const [parametros] = useSearchParams();
  const { aba } = useParams();
  const [filtro, setFiltro] = useState('');
  const [soComNotas, setSoComNotas] = useState(false);

  const contagens = notas.data || {};
  // a rota devolve lista; se vier outra coisa, a barra lateral fica vazia em vez
  // de derrubar a página inteira junto com a aba que o dev estava lendo
  const lista = useMemo(() => (Array.isArray(docs.data) ? docs.data : []), [docs.data]);
  const arquivos = useMemo(() => lista.filter((d) => d.unit_kind === 'file'), [lista]);
  const especiais = useMemo(() => lista.filter((d) => d.unit_kind !== 'file'), [lista]);

  const q = filtro.toLowerCase();
  const visiveis = arquivos.filter(
    (d) => d.unit_key.toLowerCase().includes(q) && (!soComNotas || contagens[d.unit_key]),
  );
  const aberta = aba === 'doc' ? parametros.get('key') : null;

  const abrir = (chave: string) => navegar(`/p/${slug}/doc?key=${encodeURIComponent(chave)}`);

  const Item = ({ chave, rotulo, cor, especie }: {
    chave: string;
    rotulo: string;
    cor: string;
    /** Só o grupo "Projeto" tem etiqueta: lá conviveM overview, AGENTS, decisão,
        runbook, padrão e resumo de sessão, e sem ela os títulos viram uma lista
        plana em que nada diz de que tipo é cada item. */
    especie?: string;
  }) => (
    <button
      type="button"
      className={`file-item${aberta === chave ? ' active' : ''}`}
      data-key={chave}
      onClick={() => abrir(chave)}
    >
      <span className="dot" style={{ background: cor }} />
      {!!especie && <span className="kind">{especie}</span>}
      {rotulo}
      {!!contagens[chave] && <span className="nbadge">{contagens[chave]}</span>}
    </button>
  );

  return (
    <nav className="sidebar" aria-label="Arquivos do projeto">
      <div className="side-search">
        <input
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="filtrar arquivos…"
          aria-label="Filtrar arquivos"
        />
      </div>
      <div className="side-sec" style={{ paddingTop: 0 }}>
        <label className="chk" style={{ width: '100%' }}>
          <input
            type="checkbox"
            checked={soComNotas}
            onChange={(e) => setSoComNotas(e.target.checked)}
          />
          {' '}só com notas do time
        </label>
      </div>

      <div className="side-sec">
        <h3>Projeto</h3>
        {especiais.map((d) => (
          <Item
            key={d.unit_key}
            chave={d.unit_key}
            rotulo={d.title}
            cor={COR_DA_ESPECIE[d.unit_kind] || 'var(--ink-faint)'}
            especie={d.unit_kind}
          />
        ))}
      </div>

      <div className="side-sec">
        <h3>Arquivos · {arquivos.length}</h3>
        <div id="file-list">
          {visiveis.map((d) => (
            <Item
              key={d.unit_key}
              chave={d.unit_key}
              rotulo={d.unit_key.replace(/^src\//, '')}
              cor="var(--brand)"
            />
          ))}
        </div>
        {!visiveis.length && !!arquivos.length && (
          <div className="muted" style={{ fontSize: 12, padding: '8px 10px', lineHeight: 1.5 }}>
            {soComNotas
              ? <>Nenhum dos {arquivos.length} arquivos tem nota do time{filtro ? ' com esse filtro' : ''}. Desmarque <b>só com notas</b> para ver todos.</>
              : <>Nenhum arquivo casa com “{filtro}”.</>}
          </div>
        )}
        {!arquivos.length && !docs.isPending && (
          <div className="muted" style={{ fontSize: 12, padding: '8px 10px', lineHeight: 1.5 }}>
            Nenhum arquivo documentado ainda. O acervo se enche no primeiro ingest completo do repositório.
          </div>
        )}
      </div>
    </nav>
  );
}
